import { useEffect, useState, type FormEvent } from 'react';
import type { Office } from '@/hooks/useOffice';

export interface AccountUser {
  id: string;
  username: string;
  role: string;
  is_active: boolean;
  is_owner?: boolean;
  office_ids?: number[];
  created_at?: string;
  last_sign_in_at?: string | null;
}

interface AccountModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  user: AccountUser | null;
  offices: Office[];
  onClose: () => void;
  onSubmit: (values: { username: string; password?: string; role: string; office_ids: number[] }) => Promise<void>;
}

export default function AccountModal({ open, mode, user, offices, onClose, onSubmit }: AccountModalProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('user');
  const [officeIds, setOfficeIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  useEffect(() => {
    if (open) {
      setUsername(mode === 'edit' && user ? user.username : '');
      setPassword('');
      setConfirmPassword('');
      setRole(mode === 'edit' && user ? user.role : 'user');
      setOfficeIds(mode === 'edit' && user?.office_ids ? user.office_ids : []);
      setErr('');
      setBusy(false);
      setShowPw(false);
      setShowConfirmPw(false);
    }
  }, [open, mode, user]);

  if (!open) return null;

  const isEdit = mode === 'edit';
  const isAdmin = role === 'admin';

  const toggleOffice = (id: number) => {
    setOfficeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErr('');
    if (!username.trim()) {
      setErr('Please enter a username.');
      return;
    }
    if (!isEdit && password.length < 6) {
      setErr('Password must be at least 6 characters.');
      return;
    }
    if (!isEdit && password !== confirmPassword) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        username: username.trim(),
        password: isEdit ? undefined : password,
        role,
        office_ids: officeIds,
      });
    } catch (e: any) {
      setErr(e?.message || 'Operation failed.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-base">
            {isEdit ? 'Edit account' : 'Create account'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          {isEdit ? 'Change the account name, role, and office access.' : 'Create a new account for a team member.'}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Username</label>
            <input
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setErr('');
              }}
              placeholder="e.g. jsmith"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
            />
          </div>

          {!isEdit && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErr('');
                    }}
                    placeholder="At least 6 characters"
                    className="w-full text-sm px-3 py-2 pr-10 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer w-7 h-7 flex items-center justify-center"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`${showPw ? 'ri-eye-off-line' : 'ri-eye-line'} w-4 h-4 flex items-center justify-center`} />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirmPw ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErr('');
                    }}
                    placeholder="Re-enter password"
                    className="w-full text-sm px-3 py-2 pr-10 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer w-7 h-7 flex items-center justify-center"
                    aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`${showConfirmPw ? 'ri-eye-off-line' : 'ri-eye-line'} w-4 h-4 flex items-center justify-center`} />
                  </button>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Role / Permission</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'user', label: 'User', desc: 'Limited access', icon: 'ri-user-line' },
                { value: 'admin', label: 'Admin', desc: 'Full access', icon: 'ri-shield-star-line' },
              ].map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md border text-left cursor-pointer whitespace-nowrap transition ${
                    role === r.value
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <i className={`${r.icon} w-4 h-4 flex items-center justify-center shrink-0`} />
                  <span>
                    <span className="block text-sm font-medium leading-tight">{r.label}</span>
                    <span className={`block text-[11px] leading-tight ${role === r.value ? 'text-slate-300' : 'text-slate-400'}`}>
                      {r.desc}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {isAdmin ? (
            <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-500 flex items-center gap-2">
              <i className="ri-shield-star-line w-4 h-4 flex items-center justify-center shrink-0" />
              <span>Admins can access all offices automatically.</span>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Office access</label>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {offices.length === 0 ? (
                  <div className="px-3 py-2.5 text-sm text-slate-400">No offices available yet.</div>
                ) : (
                  offices.map((o) => (
                    <label
                      key={o.id}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={officeIds.includes(o.id)}
                        onChange={() => toggleOffice(o.id)}
                        className="w-4 h-4 accent-slate-900 cursor-pointer shrink-0"
                      />
                      <i className="ri-building-2-line w-4 h-4 flex items-center justify-center text-slate-500 shrink-0" />
                      <span className="flex-1 text-slate-700">{o.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                Select which offices this account can access tape data for.
              </p>
            </div>
          )}

          {err && (
            <div className="p-3 rounded-md text-sm bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer whitespace-nowrap disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" /> Saving...
                </>
              ) : (
                <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
              )}
              {!busy && <span>{isEdit ? 'Save changes' : 'Create account'}</span>}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}